import type { Hub, SeverityLevel } from '@sentry/browser';

/** What a capture carries: the scope set in `withScope` plus its own context. */
type CaptureContext = {
  level?: SeverityLevel;
  tags?: Record<string, string>;
  contexts?: Record<string, Record<string, unknown>>;
  fingerprint?: string[];
};

/** The part of Sentry's `Scope` that `@utils/errors` calls inside `withScope`. */
type IslandScope = {
  setLevel: (level: SeverityLevel) => void;
  setTag: (key: string, value: string) => void;
  setContext: (key: string, context: Record<string, unknown> | null) => void;
  setFingerprint: (fingerprint: string[]) => void;
};

const dsn = process.env.REACT_APP_SENTRY_DSN;

let hubPromise: Promise<Hub> | undefined;
let activeScope: CaptureContext | undefined;

// The SDK is ~25 KB gzipped and most page views never report anything, so
// it is loaded as its own chunk on the first capture.
const loadHub = (sentryDsn: string): Promise<Hub> => {
  hubPromise ??= import(/* webpackChunkName: "sentry" */ './islandSentryClient')
    .then(({ createIslandHub }) => createIslandHub(sentryDsn))
    .catch((error: unknown) => {
      // A chunk that failed to load is requested again on the next capture.
      hubPromise = undefined;
      throw error;
    });

  return hubPromise;
};

const mergeContext = (
  scope: CaptureContext | undefined,
  context: CaptureContext | undefined
): CaptureContext => ({
  level: context?.level ?? scope?.level,
  tags: { ...scope?.tags, ...context?.tags },
  contexts: { ...scope?.contexts, ...context?.contexts },
  fingerprint: context?.fingerprint ?? scope?.fingerprint,
});

// `webpack.widget.js` aliases `@sentry/react` to this file: it exposes the part
// of the SDK API that the island's modules call (`@utils/errors`). Without a
// DSN every call is a no-op.
export const captureException = (
  exception: unknown,
  captureContext?: CaptureContext
): void => {
  if (!dsn) {
    return;
  }

  const context = mergeContext(activeScope, captureContext);

  loadHub(dsn)
    .then((hub) => {
      hub.captureException(exception, { captureContext: context });
    })
    // Not through `logError`: a failure to report would report itself again.
    .catch(() => {});
};

export const withScope = (callback: (scope: IslandScope) => void): void => {
  const scope: CaptureContext = { tags: {}, contexts: {} };

  activeScope = scope;

  try {
    callback({
      setLevel: (level) => {
        scope.level = level;
      },
      setTag: (key, value) => {
        scope.tags = { ...scope.tags, [key]: value };
      },
      setContext: (key, context) => {
        if (context) {
          scope.contexts = { ...scope.contexts, [key]: context };
        }
      },
      setFingerprint: (fingerprint) => {
        scope.fingerprint = fingerprint;
      },
    });
  } finally {
    activeScope = undefined;
  }
};
